"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useRef, useState } from "react";
import { HiOutlineArrowSmUp, HiOutlinePaperClip } from "react-icons/hi";
import { MdOutlineSms, MdSwapHoriz } from "react-icons/md";
import { usePathname } from "next/navigation";
import TiptapEditor from "@/components/tiptap/editor";
import { Badge } from "@/components/ui";
import Button from "@/components/ui/button";
import { getActiveRecipient, getSmsBlockedMessage } from "@/lib/chat-utils";
import createId from "@/lib/create-id";
import { formatToHumanReadable } from "@/lib/format-data";
import { getMessageBadgeVariant } from "@/lib/message-type-styles";
import { cx } from "@/lib/tailwind-utils";
import { isSmsBlocked } from "@/types/chat";
import { ChatMode, ChatStatus } from "@/types/enums/chat";
import { MessageKind } from "@/types/enums/message";
import { getContextMessageType } from "@/types/message";
import { ChatAttachInput, ChatComposerFrame } from "./chat-composer-frame";
import { useChatConfigContext, useChatInputContext, useChatSuggestionsContext } from "./chat-context";
import { ChatFileAttachments } from "./chat-file-attachments";
import { useChatFilterContext, useChatModalsRefContext, useChatScrollRefContext } from "./contexts";
import { useChatData } from "./hooks";
import { ScheduleMessageDialog } from "./schedule-message-dialog";
import { ScheduledMessagesList } from "./scheduled-messages-list";
import { useSendMessageMutation, useUpdateChatModeMutation, useUpdateChatStatusMutation, useUpdatePrimaryPatientMutation } from "@/query/comms/chat";
import { useChatTipTapEditorHeightStore } from "@/stores/tiptap-editor-height-store";
export function ChatInput() {
  const $ = _c(107);
  const pathname = usePathname();
  const {
    chatId,
    size
  } = useChatConfigContext();
  let t0;
  if ($[0] !== chatId) {
    t0 = {
      chatId
    };
    $[0] = chatId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const {
    chat
  } = useChatData(t0);
  const {
    draftContent,
    draft,
    handleDraftChange,
    clearDraft,
    selectedFiles,
    handleFileChange,
    clearFiles
  } = useChatInputContext();
  const {
    suggestions
  } = useChatSuggestionsContext();
  let t1;
  if ($[2] !== chat) {
    t1 = chat && isSmsBlocked(chat) ? getSmsBlockedMessage(chat) : null;
    $[2] = chat;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const smsBlockedMessage = t1;
  const isPortalMode = !!smsBlockedMessage || chat?.mode === ChatMode.PATIENT_PORTAL;
  const previousModeRef = useRef(isPortalMode);
  const [swapTurns, setSwapTurns] = useState(0);
  let t2;
  let t3;
  if ($[4] !== isPortalMode) {
    t2 = () => {
      if (previousModeRef.current !== isPortalMode) {
        previousModeRef.current = isPortalMode;
        setSwapTurns(_temp);
      }
    };
    t3 = [isPortalMode];
    $[4] = isPortalMode;
    $[5] = t2;
    $[6] = t3;
  } else {
    t2 = $[5];
    t3 = $[6];
  }
  useEffect(t2, t3);
  const toggleChatMode = () => {
    if (!chat?.id || smsBlockedMessage || updateChatModeMutation.isPending) {
      return;
    }
    updateChatModeMutation.mutate({
      chatId: chat.id,
      mode: isPortalMode ? ChatMode.SMS : ChatMode.PATIENT_PORTAL
    });
  };
  const {
    chatModalsRef
  } = useChatModalsRefContext();
  const {
    chatMessagesRef
  } = useChatScrollRefContext();
  const {
    clearFilters
  } = useChatFilterContext();
  const sendMessageMutation = useSendMessageMutation();
  const updateChatStatusMutation = useUpdateChatStatusMutation();
  const updatePrimaryPatientMutation = useUpdatePrimaryPatientMutation();
  const updateChatModeMutation = useUpdateChatModeMutation();
  let t4;
  if ($[7] !== chat?.patients) {
    t4 = chat?.patients?.filter(_temp2) ?? [];
    $[7] = chat?.patients;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const activePatients = t4;
  let t5;
  if ($[9] !== chat) {
    t5 = getActiveRecipient(chat);
    $[9] = chat;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const portalRecipient = t5;
  const needsPortalRecipient = isPortalMode && !portalRecipient && activePatients.length > 1;
  const noPortalRecipient = isPortalMode && activePatients.length === 0;
  let t6;
  if ($[11] !== chat || $[12] !== chatMessagesRef || $[13] !== chatModalsRef || $[14] !== clearDraft || $[15] !== clearFiles || $[16] !== clearFilters || $[17] !== draft?.message || $[18] !== draftContent || $[19] !== isPortalMode || $[20] !== needsPortalRecipient || $[21] !== noPortalRecipient || $[22] !== portalRecipient?.id || $[23] !== selectedFiles || $[24] !== sendMessageMutation || $[25] !== updateChatStatusMutation) {
    t6 = async () => {
      if (!chat || !draftContent.trim() && selectedFiles.length === 0) {
        return;
      }
      if (noPortalRecipient) {
        return;
      }
      if (needsPortalRecipient) {
        chatModalsRef.current?.openPrimaryPatientModal();
        return;
      }
      clearDraft();
      clearFiles();
      updateChatStatusMutation.mutateAsync({
        chatId: chat.id,
        status: ChatStatus.READ
      });
      const messageUid = createId("msg");
      sendMessageMutation.mutateAsync({
        chatId: chat.id,
        content: draftContent.trim(),
        files: selectedFiles,
        message: draft?.message ?? {
          kind: MessageKind.DEFAULT
        },
        messageUid,
        mode: isPortalMode ? ChatMode.PATIENT_PORTAL : ChatMode.SMS,
        recipientPatientId: isPortalMode ? portalRecipient?.id : undefined
      });
      clearFilters();
      chatMessagesRef.current?.scrollToBottom(true);
    };
    $[11] = chat;
    $[12] = chatMessagesRef;
    $[13] = chatModalsRef;
    $[14] = clearDraft;
    $[15] = clearFiles;
    $[16] = clearFilters;
    $[17] = draft?.message;
    $[18] = draftContent;
    $[19] = isPortalMode;
    $[20] = needsPortalRecipient;
    $[21] = noPortalRecipient;
    $[22] = portalRecipient?.id;
    $[23] = selectedFiles;
    $[24] = sendMessageMutation;
    $[25] = updateChatStatusMutation;
    $[26] = t6;
  } else {
    t6 = $[26];
  }
  portalRecipient?.id;
  const sendMessage = t6;
  let t7;
  if ($[27] !== chat || $[28] !== updatePrimaryPatientMutation) {
    t7 = patientId => {
      if (!chat) {
        return;
      }
      updatePrimaryPatientMutation.mutate({
        chatId: chat.id,
        patientId
      });
    };
    $[27] = chat;
    $[28] = updatePrimaryPatientMutation;
    $[29] = t7;
  } else {
    t7 = $[29];
  }
  const handleAutoSelectPrimaryPatient = t7;
  const editorContainerRef = useRef(null);
  const {
    setHeight
  } = useChatTipTapEditorHeightStore();
  let t8;
  if ($[30] !== handleDraftChange) {
    t8 = (_text, _html, completeText) => {
      handleDraftChange(completeText);
    };
    $[30] = handleDraftChange;
    $[31] = t8;
  } else {
    t8 = $[31];
  }
  const handleContentUpdate = t8;
  let t10;
  let t9;
  if ($[32] !== chatId || $[33] !== setHeight) {
    t9 = () => {
      if (!editorContainerRef.current || !chatId) {
        return;
      }
      const resizeObserver = new ResizeObserver(entries => {
        for (const entry of entries) {
          const height = entry.contentRect.height;
          setHeight(height);
        }
      });
      resizeObserver.observe(editorContainerRef.current);
      return () => {
        resizeObserver.disconnect();
      };
    };
    t10 = [setHeight, chatId];
    $[32] = chatId;
    $[33] = setHeight;
    $[34] = t10;
    $[35] = t9;
  } else {
    t10 = $[34];
    t9 = $[35];
  }
  useEffect(t9, t10);
  const t11 = size === "sm" ? "px-4" : "px-8";
  let t12;
  if ($[36] !== t11) {
    t12 = cx("py-2 absolute inset-x-0 bottom-0 z-10", "before:pointer-events-none before:absolute before:inset-x-0 before:bottom-0 before:h-12 before:bg-gradient-to-t before:from-background before:to-transparent", t11);
    $[36] = t11;
    $[37] = t12;
  } else {
    t12 = $[37];
  }
  let t13;
  if ($[38] !== chat) {
    t13 = chat && <ScheduledMessagesList chatId={chat.id} />;
    $[38] = chat;
    $[39] = t13;
  } else {
    t13 = $[39];
  }
  let t14;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <ChatFileAttachments />;
    $[40] = t14;
  } else {
    t14 = $[40];
  }
  let t15;
  if ($[41] !== chat || $[42] !== isPortalMode || $[43] !== smsBlockedMessage || $[44] !== swapTurns || $[45] !== toggleChatMode || $[46] !== updateChatModeMutation) {
    t15 = chat && <button type="button" onClick={toggleChatMode} disabled={!!smsBlockedMessage || updateChatModeMutation.isPending} data-testid="chat-mode-indicator" data-mode={isPortalMode ? "patient_portal" : "sms"} aria-label={smsBlockedMessage ? "Patient portal" : isPortalMode ? "Switch to Text" : "Switch to Patient Portal"} title={smsBlockedMessage ? `Sending through the patient portal. ${smsBlockedMessage}` : isPortalMode ? "Sending through the patient portal \u2014 click to switch to text" : "Sending as a text message \u2014 click to switch to the patient portal"} className={cx("group/mode mb-1.5 flex items-center gap-1.5 self-start rounded-full border px-2.5 py-1 text-caption font-medium", "transition-all duration-300", smsBlockedMessage ? "cursor-default" : "cursor-pointer hover:brightness-110 disabled:opacity-60", isPortalMode ? "border-primary/40 bg-primary/10 text-primary" : "border-status-warning-text/30 bg-status-warning-bg text-status-warning-text")}><span key={isPortalMode ? "patient_portal" : "sms"} className="flex animate-stepFadeIn items-center gap-1.5">{isPortalMode ? <span>Patient portal</span> : <><MdOutlineSms className="size-3.5 shrink-0" /><span>Text</span></>}</span>{!smsBlockedMessage && <MdSwapHoriz aria-hidden={true} style={{
        transform: `rotate(${swapTurns * 180}deg)`
      }} className="size-4 shrink-0 opacity-70 transition-transform duration-300 group-hover/mode:opacity-100" />}</button>;
    $[41] = chat;
    $[42] = isPortalMode;
    $[43] = smsBlockedMessage;
    $[44] = swapTurns;
    $[45] = toggleChatMode;
    $[46] = updateChatModeMutation;
    $[47] = t15;
  } else {
    t15 = $[47];
  }
  const t16 = size === "sm" ? "max-h-20" : "max-h-40";
  let t17;
  if ($[48] !== t16) {
    t17 = cx("flex-1 border-none outline-none min-h-0", t16);
    $[48] = t16;
    $[49] = t17;
  } else {
    t17 = $[49];
  }
  const t18 = isPortalMode ? "Type a secure portal message\u2026" : "Type here...";
  let t19;
  if ($[50] !== activePatients || $[51] !== chat?.primaryPatientId || $[52] !== chatModalsRef || $[53] !== handleAutoSelectPrimaryPatient || $[54] !== pathname || $[55] !== portalRecipient) {
    t19 = () => {
      if (pathname?.includes("/patient/merge")) {
        return true;
      }
      if (!portalRecipient) {
        if (activePatients.length > 1) {
          chatModalsRef.current?.openPrimaryPatientModal();
          return false;
        }
      } else {
        if (portalRecipient.id !== chat?.primaryPatientId) {
          handleAutoSelectPrimaryPatient(portalRecipient.id);
        }
      }
      return true;
    };
    $[50] = activePatients;
    $[51] = chat?.primaryPatientId;
    $[52] = chatModalsRef;
    $[53] = handleAutoSelectPrimaryPatient;
    $[54] = pathname;
    $[55] = portalRecipient;
    $[56] = t19;
  } else {
    t19 = $[56];
  }
  let t20;
  if ($[57] !== draftContent || $[58] !== handleContentUpdate || $[59] !== sendMessage || $[60] !== suggestions || $[61] !== t17 || $[62] !== t18 || $[63] !== t19) {
    t20 = <TiptapEditor editorClassName={t17} parentClassName="flex-1 min-w-0" content={draftContent} onUpdate={handleContentUpdate} onEnter={sendMessage} suggestions={suggestions} expandSuggestionsOnInsert={true} textArea={false} focusClasses={false} placeholder={t18} posthogSurface="sms" onSuggestionOpen={t19} />;
    $[57] = draftContent;
    $[58] = handleContentUpdate;
    $[59] = sendMessage;
    $[60] = suggestions;
    $[61] = t17;
    $[62] = t18;
    $[63] = t19;
    $[64] = t20;
  } else {
    t20 = $[64];
  }
  const t21 = "fileInput" + chatId;
  let t22;
  if ($[65] !== handleFileChange || $[66] !== t21) {
    t22 = <ChatAttachInput inputId={t21} onChange={handleFileChange}>{_temp3}</ChatAttachInput>;
    $[65] = handleFileChange;
    $[66] = t21;
    $[67] = t22;
  } else {
    t22 = $[67];
  }
  let t23;
  if ($[68] !== chat || $[69] !== clearDraft || $[70] !== draftContent || $[71] !== isPortalMode || $[72] !== selectedFiles) {
    t23 = chat && !isPortalMode && <ScheduleMessageDialog chatId={chat.id} content={draftContent} disabled={!draftContent.trim() || selectedFiles.length > 0} onScheduled={() => {
      clearDraft();
    }} />;
    $[68] = chat;
    $[69] = clearDraft;
    $[70] = draftContent;
    $[71] = isPortalMode;
    $[72] = selectedFiles;
    $[73] = t23;
  } else {
    t23 = $[73];
  }
  let t24;
  if ($[74] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <HiOutlineArrowSmUp className="size-3.5" />;
    $[74] = t24;
  } else {
    t24 = $[74];
  }
  let t25;
  if ($[75] !== draftContent || $[76] !== selectedFiles) {
    t25 = !draftContent.trim() && selectedFiles.length === 0;
    $[75] = draftContent;
    $[76] = selectedFiles;
    $[77] = t25;
  } else {
    t25 = $[77];
  }
  let t26;
  if ($[78] !== sendMessage || $[79] !== t25) {
    t26 = <Button variant="secondary" onClick={sendMessage} icon={t24} disabled={t25} className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-background/50 p-0" />;
    $[78] = sendMessage;
    $[79] = t25;
    $[80] = t26;
  } else {
    t26 = $[80];
  }
  let t27;
  if ($[81] !== draft) {
    t27 = draft && getContextMessageType(draft.message) && <Badge variant={getMessageBadgeVariant(getContextMessageType(draft.message))} className="absolute -top-3 left-1 z-10 text-caption">{formatToHumanReadable(getContextMessageType(draft.message))}</Badge>;
    $[81] = draft;
    $[82] = t27;
  } else {
    t27 = $[82];
  }
  let t28;
  if ($[83] !== isPortalMode || $[84] !== size || $[85] !== t20 || $[86] !== t22 || $[87] !== t23 || $[88] !== t26 || $[89] !== t27) {
    t28 = <ChatComposerFrame ref={editorContainerRef} size={size} emphasized={isPortalMode}>{t20}{t22}{t23}{t26}{t27}</ChatComposerFrame>;
    $[83] = isPortalMode;
    $[84] = size;
    $[85] = t20;
    $[86] = t22;
    $[87] = t23;
    $[88] = t26;
    $[89] = t27;
    $[90] = t28;
  } else {
    t28 = $[90];
  }
  let t29;
  if ($[91] !== t15 || $[92] !== t28) {
    t29 = <div className="flex w-full flex-col">{t15}{t28}</div>;
    $[91] = t15;
    $[92] = t28;
    $[93] = t29;
  } else {
    t29 = $[93];
  }
  const t30 = noPortalRecipient ? "No active patient on this conversation can receive portal messages." : needsPortalRecipient ? "Several patients share this number. Choose who this portal message is for." : smsBlockedMessage ? `${smsBlockedMessage} Messages go to the patient portal.` : "Press Enter to send, Shift+Enter for new line";
  let t31;
  if ($[94] !== t30) {
    t31 = <span>{t30}</span>;
    $[94] = t30;
    $[95] = t31;
  } else {
    t31 = $[95];
  }
  let t32;
  if ($[96] !== isPortalMode || $[97] !== portalRecipient) {
    t32 = isPortalMode && portalRecipient && <span className="ml-1">{`Only ${portalRecipient.firstName} ${portalRecipient.lastName} will see this in their portal.`}</span>;
    $[96] = isPortalMode;
    $[97] = portalRecipient;
    $[98] = t32;
  } else {
    t32 = $[98];
  }
  let t33;
  if ($[99] !== t31 || $[100] !== t32) {
    t33 = <div className="mt-1 text-caption text-foreground-muted z-10 relative">{t31}{t32}</div>;
    $[99] = t31;
    $[100] = t32;
    $[101] = t33;
  } else {
    t33 = $[101];
  }
  let t34;
  if ($[102] !== t12 || $[103] !== t13 || $[104] !== t29 || $[105] !== t33) {
    t34 = <div className={t12}>{t13}{t14}{t29}{t33}</div>;
    $[102] = t12;
    $[103] = t13;
    $[104] = t29;
    $[105] = t33;
    $[106] = t34;
  } else {
    t34 = $[106];
  }
  return t34;
}
function _temp3(open) {
  return <button type="button" onClick={open} aria-label="Attach a file" className="shrink-0 cursor-pointer"><HiOutlinePaperClip size={20} className="text-primary" /></button>;
}
function _temp2(p) {
  return !p.isArchived;
}
function _temp(turns) {
  return turns + 1;
}